import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IPE_LOGIN_PATH } from '@/config/ipeRoutes';
import { IpeBrandMark } from '@/components/ipe/IpeBrandLogo';
import IpeLocaleSwitcher from '@/i18n/IpeLocaleSwitcher';
import LegalRichText from './LegalRichText';
import '@/styles/ipe/legal.css';

export function LegalParagraph({ children, bold = false }) {
  return (
    <p className={`ipe-legal__paragraph${bold ? ' ipe-legal__intro' : ''}`}>
      {typeof children === 'string' ? <LegalRichText text={children} /> : children}
    </p>
  );
}

export function LegalSection({ title, children }) {
  return (
    <section className="ipe-legal__section">
      <h2 className="ipe-legal__section-title">{title}</h2>
      {children}
    </section>
  );
}

export function LegalSubTitle({ children }) {
  return <h3 className="ipe-legal__sub-title">{children}</h3>;
}

export function LegalList({ items, ordered = false }) {
  return (
    <ul className={`ipe-legal__list${ordered ? ' ipe-legal__list--decimal' : ''}`}>
      {(items || []).map((item, index) => (
        <li key={`${index}-${String(item).slice(0, 24)}`}>
          <LegalRichText text={item} />
        </li>
      ))}
    </ul>
  );
}

export function LegalDocBlocks({ blocks }) {
  return (blocks || []).map((block, index) => {
    if (block.type === 'subtitle') {
      return <LegalSubTitle key={`s-${index}`}>{block.text}</LegalSubTitle>;
    }
    if (block.type === 'list') {
      return <LegalList key={`l-${index}`} items={block.items} ordered={Boolean(block.ordered)} />;
    }
    return (
      <LegalParagraph key={`p-${index}`} bold={Boolean(block.bold)}>
        {block.text}
      </LegalParagraph>
    );
  });
}

export default function LegalPageLayout({
  title,
  updateDate,
  intro,
  children,
  otherDocLink,
  otherDocLabel,
  updatedAtLabel,
  backToLoginLabel,
}) {
  const { t } = useTranslation('login');
  const updatedAt = updatedAtLabel || t('legal.updatedAt', { date: updateDate, defaultValue: `更新日期：${updateDate}` });
  const backToLogin = backToLoginLabel || t('legal.backToLogin', { defaultValue: '返回登录' });

  return (
    <div className="ipe-legal">
      <header className="ipe-legal__topbar">
        <IpeBrandMark className="ipe-legal__brand" textClassName="ipe-legal__brand-text" text="IPE" />
        <nav className="ipe-legal__nav">
          <span data-ipe-locale-dock="">
            <IpeLocaleSwitcher />
          </span>
          <Link to={otherDocLink}>{otherDocLabel}</Link>
          <Link to={IPE_LOGIN_PATH}>{backToLogin}</Link>
        </nav>
      </header>

      <div className="ipe-legal__wrap">
        <article className="ipe-legal__card">
          <h1 className="ipe-legal__title">{title}</h1>
          <p className="ipe-legal__date">{updatedAt}</p>
          {intro ? (
            <p className="ipe-legal__intro">
              <LegalRichText text={intro} />
            </p>
          ) : null}
          {children}
        </article>
      </div>

      <footer className="ipe-legal__footer">
        <Link to={otherDocLink}>{otherDocLabel}</Link>
        <span aria-hidden="true">·</span>
        <Link to={IPE_LOGIN_PATH}>{backToLogin}</Link>
      </footer>
    </div>
  );
}
